import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import ProductCard from "../components/ProductCard";
import { Search } from "lucide-react";

export default function Marketplace() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState({ categories: [], regions: [] });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        q: searchParams.get("q") || "",
        category: searchParams.get("category") || "",
        region: searchParams.get("region") || "",
        min_price: "",
        max_price: "",
        limited: false,
        sort: "newest",
    });

    useEffect(() => {
        api.get("/products/categories").then((r) => setCats(r.data));
    }, []);

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            const params = {};
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== "" && v !== false && v !== null) params[k] = v;
            });
            const { data } = await api.get("/products", { params });
            setProducts(data);
            setLoading(false);
        };
        fetch();
        const sp = new URLSearchParams();
        if (filters.q) sp.set("q", filters.q);
        if (filters.category) sp.set("category", filters.category);
        if (filters.region) sp.set("region", filters.region);
        setSearchParams(sp, { replace: true });
    }, [filters, setSearchParams]);

    function set(k, v) {
        setFilters((f) => ({ ...f, [k]: v }));
    }

    return (
        <>
            <Navbar variant="light" />
            <div className="pt-32 pb-16 bg-beige-natural border-b border-wine-dark/10">
                <div className="container-edge">
                    <span className="label-overline text-wine">Marketplace</span>
                    <h1 className="mt-4 font-heading text-5xl md:text-7xl text-wine-dark leading-[0.95]">
                        Despensa portuguesa
                    </h1>
                    <p className="mt-6 max-w-xl text-wine-dark/70">
                        {products.length} {products.length === 1 ? "produto" : "produtos"} de
                        pequenos produtores portugueses, selecionados à mão.
                    </p>
                </div>
            </div>

            <div className="container-edge py-12 grid grid-cols-1 lg:grid-cols-12 gap-12 bg-beige-natural">
                {/* Filters */}
                <aside className="lg:col-span-3 space-y-8" data-testid="filters-panel">
                    <div>
                        <label className="label-overline text-wine-dark block mb-3">Pesquisar</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wine/60" strokeWidth={1.5} />
                            <input
                                value={filters.q}
                                onChange={(e) => set("q", e.target.value)}
                                placeholder="Vinho, azeite…"
                                className="w-full bg-transparent border border-wine-dark/20 pl-10 pr-3 py-3 text-sm focus:border-wine"
                                data-testid="search-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="label-overline text-wine-dark block mb-3">Categoria</label>
                        <div className="space-y-2">
                            <FilterRadio name="cat" label="Todas" checked={!filters.category} onChange={() => set("category", "")} />
                            {cats.categories.map((c) => (
                                <FilterRadio
                                    key={c}
                                    name="cat"
                                    label={c}
                                    checked={filters.category === c}
                                    onChange={() => set("category", c)}
                                    testId={`filter-cat-${c}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-overline text-wine-dark block mb-3">Região</label>
                        <div className="space-y-2">
                            <FilterRadio name="reg" label="Todas" checked={!filters.region} onChange={() => set("region", "")} />
                            {cats.regions.map((r) => (
                                <FilterRadio
                                    key={r}
                                    name="reg"
                                    label={r}
                                    checked={filters.region === r}
                                    onChange={() => set("region", r)}
                                    testId={`filter-region-${r}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-overline text-wine-dark block mb-3">Preço</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={filters.min_price}
                                onChange={(e) => set("min_price", e.target.value)}
                                placeholder="Min €"
                                className="w-full bg-transparent border border-wine-dark/20 px-3 py-3 text-sm focus:border-wine"
                            />
                            <input
                                type="number"
                                value={filters.max_price}
                                onChange={(e) => set("max_price", e.target.value)}
                                placeholder="Max €"
                                className="w-full bg-transparent border border-wine-dark/20 px-3 py-3 text-sm focus:border-wine"
                            />
                        </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={filters.limited}
                            onChange={(e) => set("limited", e.target.checked)}
                            className="accent-wine"
                            data-testid="filter-limited"
                        />
                        <span className="text-sm text-wine-dark">Apenas edições limitadas</span>
                    </label>
                </aside>

                {/* Products */}
                <section className="lg:col-span-9">
                    <div className="flex items-center justify-between mb-8">
                        <span className="label-overline text-wine-dark/60">
                            {products.length} resultados
                        </span>
                        <select
                            value={filters.sort}
                            onChange={(e) => set("sort", e.target.value)}
                            className="bg-transparent border border-wine-dark/20 px-3 py-2 text-sm focus:border-wine"
                            data-testid="sort-select"
                        >
                            <option value="newest">Mais recentes</option>
                            <option value="price_asc">Preço: menor</option>
                            <option value="price_desc">Preço: maior</option>
                        </select>
                    </div>
                    {loading ? (
                        <div className="text-wine-dark/50 label-overline">A carregar…</div>
                    ) : products.length === 0 ? (
                        <div className="text-wine-dark/60 py-20 text-center">Nenhum produto encontrado.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    )}
                </section>
            </div>
            <Footer />
        </>
    );
}

function FilterRadio({ name, label, checked, onChange, testId }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer group" data-testid={testId}>
            <input type="radio" name={name} checked={checked} onChange={onChange} className="accent-wine" />
            <span className={`text-sm transition-colors ${checked ? "text-wine font-semibold" : "text-wine-dark/70 group-hover:text-wine"}`}>
                {label}
            </span>
        </label>
    );
}
